import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { CountLink } from '../components/CountLink';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, SUB_SX, TABLE_SX, growthCell, levelCell, scoredSample } from '../components/GradeTable';
import { SmallGroupMarker } from '../components/SmallGroupMarker';
import { gradeName, schoolComponentOf, schoolHref } from '../listKeys';
import type { PeGradeDetail, ScoreUnit } from '../types';
import { EM_DASH, fmtDelta, fmtGrowthIn, fmtLevel, growthNoun, levelNoun } from '../utils/drilldownFormat';
import {
  DetailCard,
  DetailStrip,
  DrilldownFrame,
  DrilldownPage,
  NotFoundPage,
  StudentsLinkCard,
  sameDataSentence,
  type LoadedReport,
} from './DrilldownFrame';

const SMALL_FOOT =
  'The info marker means a small group: one student’s score can move its average a lot. Small groups don’t produce findings; every row still shows its numbers.';

function narrativeFor(grade: PeGradeDetail, name: string, resetYear: boolean, unit: ScoreUnit, scoresAbsent: boolean): string | undefined {
  if (scoresAbsent || grade.small) return undefined;
  if (resetYear) {
    const level = unit === 'scale_score' ? grade.level_scale : grade.level;
    if (scoredSample(grade, unit) === 0 || level == null) return undefined;
    return `${name} sits at ${levelCell(grade.level, grade.level_scale, unit)} on the new ACCESS scale. Growth against last year’s groups returns with the Spring 2027 results.`;
  }
  const growthOf = (g: { growth: number | null; growth_scale: number | null }) => (unit === 'scale_score' ? g.growth_scale : g.growth);
  const lastOf = (g: { last_growth: number | null; last_growth_scale: number | null }) => (unit === 'scale_score' ? g.last_growth_scale : g.last_growth);
  if (growthOf(grade) == null) return undefined;
  const level3 = grade.starts.find((s) => s.bucket === '3');
  if (level3 && level3.count > 0 && !level3.small && growthOf(level3) != null && lastOf(level3) != null) {
    return `Students who started at level 3 grew ${fmtGrowthIn(unit, level3.growth, level3.growth_scale)} this year, against ${fmtGrowthIn(unit, level3.last_growth, level3.last_growth_scale)} for last year’s ${name.toLowerCase()} students who started there.`;
  }
  const against = lastOf(grade) != null ? `against ${fmtGrowthIn(unit, grade.last_growth, grade.last_growth_scale)} for the same grade last year` : 'with no comparable group last year';
  return `${name} grew ${fmtGrowthIn(unit, grade.growth, grade.growth_scale)} this year, ${against}.`;
}

function GradeBody({ loaded, grade }: { loaded: LoadedReport; grade: PeGradeDetail }) {
  const { resetYear, settings, generatedOn, unit, report } = loaded;
  const scoresAbsent = report.assessment_data_absent;
  const threshold = settings.smallGroupThreshold;
  const name = gradeName(grade.grade, grade.grade_label);
  const growth = (value: number | null, scale: number | null, count: number) => (scoresAbsent ? EM_DASH : growthCell(value, scale, unit, resetYear, count));
  const compact = (value: number | null, scale: number | null, count: number) => growth(value, scale, count).replace(/ (levels|pts)$/, '');
  const scored = scoredSample(grade, unit);
  const anySmall = grade.starts.some((s) => s.small && s.count > 0);
  const others = grade.others;
  const othersCount = others?.count ?? Math.max(0, grade.count - grade.cohorts.reduce((sum, c) => sum + c.count, 0));

  return (
    <DrilldownPage crumbs={[{ label: name }]} title={name} subtitle={sameDataSentence(generatedOn)}>
      <DetailStrip
        items={[
          {
            label: 'EL students',
            value: (
              <>
                <CountLink count={grade.count} listKey={grade.key} />
                {scored < grade.count ? ` · ${scored} scored` : ''}
              </>
            ),
          },
          { label: `Composite ${levelNoun(unit)}`, value: levelCell(grade.level, grade.level_scale, unit) },
          { label: 'Growth this year', value: growth(grade.growth, grade.growth_scale, scored) },
          { label: 'Same grade last year', value: growth(grade.last_growth, grade.last_growth_scale, 0) },
          {
            label: 'Met exit criteria',
            value: scoresAbsent ? EM_DASH : <CountLink count={grade.met} listKey={`${grade.key}:met`} />,
          },
        ]}
      />

      <DetailCard
        title="By starting level"
        description={`${name} students grouped by their composite level on last year’s ACCESS. Growth slows as level rises, so compare each group with the same group last year.`}
        foot={anySmall ? SMALL_FOOT : undefined}
        narrative={narrativeFor(grade, name, resetYear, unit, scoresAbsent)}
      >
        <Box data-testid="by-start-table" sx={TABLE_SX}>
          <Box sx={HEAD_ROW_SX}>
            <Box sx={FIRST_CELL_SX}>Started at</Box>
            <Box sx={{ ...CELL_SX, width: 120 }}>Students</Box>
            <Box sx={CELL_SX}>Growth this year</Box>
            <Box sx={{ ...CELL_SX, width: 170 }}>Same group last year</Box>
            <Box sx={{ ...CELL_SX, width: 120 }}>Met exit criteria</Box>
          </Box>
          {grade.starts.map((s) => (
            <Box key={s.key} sx={ROW_SX}>
              <Box sx={FIRST_CELL_SX}>{s.label}</Box>
              <Box sx={{ ...CELL_SX, width: 120 }}>
                <CountLink count={s.count} listKey={s.key} />
                {s.small && s.count > 0 && <SmallGroupMarker threshold={threshold} />}
              </Box>
              <Box sx={CELL_SX}>{resetYear || scoresAbsent ? EM_DASH : s.bucket === 'none' ? (scoredSample(s, unit) > 0 ? 'First year tested' : EM_DASH) : fmtGrowthIn(unit, s.growth, s.growth_scale)}</Box>
              <Box sx={{ ...CELL_SX, width: 170, color: COLORS.muted }}>
                {resetYear || scoresAbsent || s.bucket === 'none' ? EM_DASH : fmtGrowthIn(unit, s.last_growth, s.last_growth_scale)}
              </Box>
              <Box sx={{ ...CELL_SX, width: 120 }}>{scoresAbsent ? EM_DASH : s.met}</Box>
            </Box>
          ))}
        </Box>
      </DetailCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <DetailCard
          title="By cohort"
          description="Newcomers and long-term ELs in the same grade are very different groups."
          foot={`Growth in ${growthNoun(unit)}. Newcomers in their first year have no prior-year score.`}
        >
          <Box data-testid="by-cohort-table" sx={TABLE_SX}>
            <Box sx={HEAD_ROW_SX}>
              <Box sx={FIRST_CELL_SX}>Cohort</Box>
              <Box sx={{ ...CELL_SX, width: 100 }}>Students</Box>
              <Box sx={{ ...CELL_SX, width: 90 }}>Growth</Box>
              <Box sx={{ ...CELL_SX, width: 90 }}>Last year</Box>
            </Box>
            {grade.cohorts.map((c) => (
              <Box key={c.key} sx={ROW_SX}>
                <Box sx={FIRST_CELL_SX}>{c.label}</Box>
                <Box sx={{ ...CELL_SX, width: 100 }}>
                  <CountLink count={c.count} listKey={c.key} />
                  {c.small && c.count > 0 && <SmallGroupMarker threshold={threshold} />}
                </Box>
                <Box sx={{ ...CELL_SX, width: 90 }}>{c.count === 0 ? EM_DASH : compact(c.growth, c.growth_scale, scoredSample(c, unit))}</Box>
                <Box sx={{ ...CELL_SX, width: 90, color: COLORS.muted }}>{c.count === 0 ? EM_DASH : compact(c.last_growth, c.last_growth_scale, 0)}</Box>
              </Box>
            ))}
            <Box sx={ROW_SX}>
              <Box sx={FIRST_CELL_SX}>All other ELs</Box>
              <Box sx={{ ...CELL_SX, width: 100 }}>
                {others ? <CountLink count={othersCount} listKey={others.key} /> : othersCount}
              </Box>
              <Box sx={{ ...CELL_SX, width: 90 }}>{others && othersCount > 0 ? compact(others.growth, others.growth_scale, scoredSample(others, unit)) : EM_DASH}</Box>
              <Box sx={{ ...CELL_SX, width: 90, color: COLORS.muted }}>
                {others && othersCount > 0 ? compact(others.last_growth, others.last_growth_scale, 0) : EM_DASH}
              </Box>
            </Box>
          </Box>
        </DetailCard>

        <DetailCard
          title="By domain"
          description={`${name}’s four ACCESS domains, against ${name.toLowerCase()} last year.`}
          foot="Average proficiency level and growth in levels, by domain; the server publishes domain rows in levels only."
        >
          <Box data-testid="by-domain-table" sx={TABLE_SX}>
            <Box sx={HEAD_ROW_SX}>
              <Box sx={FIRST_CELL_SX}>Domain</Box>
              <Box sx={{ ...CELL_SX, width: 90 }}>Level</Box>
              <Box sx={{ ...CELL_SX, width: 90 }}>Growth</Box>
              <Box sx={{ ...CELL_SX, width: 90 }}>Last year</Box>
            </Box>
            {grade.domains.map((d) => (
              <Box key={d.domain} sx={ROW_SX}>
                <Box sx={FIRST_CELL_SX}>
                  {d.label}
                  {d.scored !== undefined && d.scored !== grade.scored && (
                    <Typography component="span" sx={SUB_SX}>{`${d.scored} scored`}</Typography>
                  )}
                </Box>
                <Box sx={{ ...CELL_SX, width: 90 }}>{fmtLevel(d.level)}</Box>
                <Box sx={{ ...CELL_SX, width: 90 }}>{resetYear ? EM_DASH : fmtDelta(d.growth, '')}</Box>
                <Box sx={{ ...CELL_SX, width: 90, color: COLORS.muted }}>{resetYear ? EM_DASH : fmtDelta(d.last_growth, '')}</Box>
              </Box>
            ))}
          </Box>
        </DetailCard>
      </Box>

      <DetailCard
        title="By school"
        description={`${name} at each school, against that school’s ${name.toLowerCase()} last year. Open a school to see all its grades.`}
      >
        <Box data-testid="by-school-table" sx={TABLE_SX}>
          <Box sx={HEAD_ROW_SX}>
            <Box sx={FIRST_CELL_SX}>School</Box>
            <Box sx={{ ...CELL_SX, width: 120 }}>Students</Box>
            <Box sx={CELL_SX}>Growth this year</Box>
            <Box sx={{ ...CELL_SX, width: 170 }}>Same grade last year</Box>
            <Box sx={{ ...CELL_SX, width: 120 }}>Met exit criteria</Box>
          </Box>
          {grade.schools.map((s) => (
            <Box key={s.key} sx={ROW_SX}>
              <Box sx={FIRST_CELL_SX}>
                <Link
                  component={RouterLink}
                  to={schoolHref(schoolComponentOf(s.key))}
                  state={APP_LINK_STATE}
                  underline="hover"
                  sx={{ color: COLORS.link, fontWeight: 600 }}
                >
                  {s.school}
                </Link>
              </Box>
              <Box sx={{ ...CELL_SX, width: 120 }}>
                <CountLink count={s.count} listKey={s.key} />
                {s.small && s.count > 0 && <SmallGroupMarker threshold={threshold} />}
              </Box>
              <Box sx={CELL_SX}>{growth(s.growth, s.growth_scale, scoredSample(s, unit))}</Box>
              <Box sx={{ ...CELL_SX, width: 170, color: COLORS.muted }}>{growth(s.last_growth, s.last_growth_scale, 0)}</Box>
              <Box sx={{ ...CELL_SX, width: 120 }}>{scoresAbsent ? EM_DASH : <CountLink count={s.met} listKey={`${s.key}:met`} />}</Box>
            </Box>
          ))}
        </Box>
      </DetailCard>

      <StudentsLinkCard count={grade.count} listKey={grade.key} noun="in this grade" />
    </DrilldownPage>
  );
}

export function GradeDetailPage() {
  const { grade: param = '' } = useParams<{ grade: string }>();
  const crumbs = [{ label: gradeName(param) }];
  return (
    <DrilldownFrame crumbs={crumbs}>
      {(loaded) => {
        const grade = loaded.drilldown.grades.find(
          (g) => String(g.grade) === param || g.grade_label === param,
        );
        if (!grade) {
          return (
            <NotFoundPage
              crumbs={crumbs}
              loaded={loaded}
              message={`${gradeName(param)} has no EL students in ${loaded.schoolYear}.`}
            />
          );
        }
        return <GradeBody loaded={loaded} grade={grade} />;
      }}
    </DrilldownFrame>
  );
}
